import React from 'react';
import type { Maybe } from '@soundxyz/utils/helpers';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { isOpenEdition } from '@components/Song/MintComponents/shared-helpers';
import { shouldShowEndsInCountdown } from '@components/SongCards/ReleaseInfoCard';
import { SongCard } from '@components/basics/SongCard';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, SearchResultReleaseFragmentDoc } from '@graphql/reactQuery';
import { setUpNextTrackIds } from '@store/audio/AudioQueue';
import { ReleaseCardActions } from './ReleaseCardActions';
import { SaleCountdown } from './ReleaseInfoCard';
import { MintStartCountdown } from './UpcomingDropCard';
import { useReleaseCardInfo } from './useReleaseCardInfo';

/** Helper that decide if we should show Running Info component  */
export const shouldShowRunningSection = ({
  finalQuantity,
  quantityUpperBound,
  quantityLowerBound,
}: {
  finalQuantity: Maybe<number>;
  quantityUpperBound: number;
  quantityLowerBound: number;
}) => {
  const currentMaxQuantity = finalQuantity ?? quantityUpperBound;
  const isOpen = isOpenEdition(currentMaxQuantity);
  // range edition is when lower and upper bounds are different
  // which is also open edition but OE are special range upper bound
  const isRange = quantityLowerBound < currentMaxQuantity && !isOpen;
  const showEditionRunning = (isOpen || isRange) && !finalQuantity;
  return showEditionRunning;
};

export const SearchSongCard = ({
  data,
  getNextTrackIds,
}: {
  data: FragmentType<SearchResultReleaseFragmentDoc>;
  getNextTrackIds: (() => string[]) | undefined;
}) => {
  const release = getFragment(SearchResultReleaseFragmentDoc, data);

  const {
    mintStartTimestamp,
    supplyCutoffTimestamp,
    quantityLowerBound,
    quantityUpperBound,
    finalQuantity,
  } = release;

  const now = Date.now();
  const hasSaleStarted = now > mintStartTimestamp;

  const showCountdown = shouldShowEndsInCountdown({
    finalQuantity,
    quantityUpperBound,
    quantityLowerBound,
    mintStartTimestamp,
  });

  const header = hasSaleStarted ? (
    showCountdown ? (
      <SaleCountdown endTimeMs={supplyCutoffTimestamp ?? 0} />
    ) : null
  ) : (
    <MintStartCountdown mintStartTimestamp={mintStartTimestamp ?? 0} />
  );

  const {
    coverImageUrl,
    trackUrl,
    onPlayClick,
    isAudioBuffering: isBufferingAudio,
    playButtonPrefetchRef,
    isActiveTrack,
    isAlbum,
    hasAudioAccess,
    artistName,
    artistWebappPath,
    releaseTitle,
  } = useReleaseCardInfo({ data: release });

  const onPlayButtonClick = useStableCallback(async () => {
    await onPlayClick();
    if (getNextTrackIds) setUpNextTrackIds(getNextTrackIds());
  });

  return (
    <SongCard
      album={isAlbum}
      isPlaying={isActiveTrack}
      loading={false}
      edition={false}
      expanded={null}
      setExpanded={null}
    >
      <SongCard.Cover
        header={header}
        coverLink={trackUrl}
        image={<SongCard.Cover.Single src={coverImageUrl} />}
        onPlayButtonClick={onPlayButtonClick}
        isBufferingSong={isBufferingAudio}
        disablePlay={!hasAudioAccess}
        playButtonPrefetchRef={playButtonPrefetchRef}
      />
      <SongCard.Info
        header={
          <AccessibleLink absoluteUri={trackUrl} type="external">
            {releaseTitle}
          </AccessibleLink>
        }
        subheader={<AccessibleLink pathnameUri={artistWebappPath}>{artistName}</AccessibleLink>}
        actions={<ReleaseCardActions data={release} />}
      />
    </SongCard>
  );
};
